import { useMemo, useState } from "react"

import { useKeywordAnalytics, type KeywordRow } from "@/api/hooks/adsManager"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { formatCellValue } from "../lib/formatters"

interface KeywordsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  campaignId: string
  campaignName: string
  startDate: string
  endDate: string
  selectedUserIds?: number[]
}

/** Matches `/api/analytics/keywords` payload (ADW keyword stats). */
const KEYWORD_COLUMNS = [
  { id: "keyword", label: "Keyword" },
  { id: "gclicks", label: "gClick" },
  { id: "rpc", label: "RPC" },
  { id: "revenue", label: "Revenue" },
] as const

export function KeywordsModal({
  open,
  onOpenChange,
  campaignId,
  campaignName,
  startDate,
  endDate,
  selectedUserIds = [],
}: KeywordsModalProps) {
  const [sortBy, setSortBy] = useState("revenue")
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc")

  const { data, isLoading, isError } = useKeywordAnalytics(
    {
      start_date: startDate,
      end_date: endDate,
      tiktok_campaign_id: campaignId,
      sort_by: sortBy,
      sort_order: sortOrder,
      user_ids: selectedUserIds.length ? selectedUserIds : undefined,
    },
    { enabled: open && Boolean(campaignId) },
  )

  const rows: KeywordRow[] = useMemo(() => data?.data ?? [], [data])
  const totals = useMemo(() => {
    let totalRevenue = 0
    let totalGclicks = 0
    for (const row of rows) {
      totalRevenue += Number(row.revenue ?? 0)
      totalGclicks += Number(row.gclicks ?? 0)
    }
    return {
      revenue: totalRevenue,
      gclicks: totalGclicks,
      rpc: totalGclicks > 0 ? totalRevenue / totalGclicks : null,
    }
  }, [rows])

  const handleSort = (colId: string) => {
    if (sortBy === colId) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
    } else {
      setSortBy(colId)
      setSortOrder(colId === "keyword" ? "asc" : "desc")
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto border-[#4a4a4a] bg-[#2d2d2d] text-white">
        <DialogHeader>
          <DialogTitle className="text-white">
            Keywords - {campaignName}
          </DialogTitle>
          <DialogDescription className="text-white/80">
            Keyword analytics for {startDate} to {endDate}
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="py-8 text-center text-white">
            Loading keywords...
          </div>
        )}

        {isError && (
          <div className="py-8 text-center text-red-400">
            Failed to load keywords
          </div>
        )}

        {!isLoading && !isError && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="border-b border-[#4a4a4a] bg-[#3a3a3a]">
                  {KEYWORD_COLUMNS.map((col) => (
                    <th
                      key={col.id}
                      onClick={() => handleSort(col.id)}
                      className={`cursor-pointer whitespace-nowrap px-2 py-2 font-semibold text-white hover:text-white ${
                        col.id === "keyword" ? "text-left" : "text-right"
                      }`}
                    >
                      {col.label}
                      {sortBy === col.id
                        ? sortOrder === "asc"
                          ? " \u25B2"
                          : " \u25BC"
                        : ""}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={KEYWORD_COLUMNS.length}
                      className="py-6 text-center text-white/80"
                    >
                      No keywords found
                    </td>
                  </tr>
                ) : (
                  rows.map((row, i) => (
                    <tr
                      key={`${row.keyword}-${i}`}
                      className="border-b border-[#4a4a4a] hover:bg-[#3a3a3a]"
                    >
                      {KEYWORD_COLUMNS.map((col) => (
                        <td
                          key={col.id}
                          className={`whitespace-nowrap px-2 py-1.5 text-white ${
                            col.id === "keyword" ? "text-left" : "text-right"
                          }`}
                        >
                          {col.id === "keyword"
                            ? String(row.keyword ?? "")
                            : formatCellValue(col.id, row[col.id])}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr
                    style={{
                      backgroundColor: "#4a4a4a",
                      borderTop: "2px solid #8b9aff",
                    }}
                  >
                    <td className="px-2 py-2 font-semibold text-white">Total</td>
                    <td className="px-2 py-2 text-right font-semibold text-white">
                      {formatCellValue("gclicks", totals.gclicks)}
                    </td>
                    <td className="px-2 py-2 text-right font-semibold text-white">
                      {formatCellValue("rpc", totals.rpc)}
                    </td>
                    <td className="px-2 py-2 text-right font-semibold text-white">
                      {formatCellValue("revenue", totals.revenue)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
